class StickyToolsShowcaseController {
  /**
   * @param {HTMLElement} section
   */
  constructor(section) {
    this.section = section;

    /** @type {HTMLElement | null} */
    this.scrollArea = section.querySelector("[data-tools-showcase-scroll-area]");

    /** @type {HTMLElement | null} */
    this.scene = section.querySelector("[data-tools-showcase-scene]");

    /** @type {HTMLElement | null} */
    this.phone = section.querySelector("[data-tools-phone]");

    /** @type {HTMLElement | null} */
    this.content = section.querySelector("[data-tools-content]");

    /** @type {HTMLElement | null} */
    this.overlays = section.querySelector("[data-tools-showcase-overlays]");

    /** @type {HTMLElement[]} */
    this.cards = Array.from(
      section.querySelectorAll(".sticky-tools-card")
    ).filter((card) => card instanceof HTMLElement);

    /** @type {number | null} */
    this.raf = null;

    this.metrics = {
      sceneHeight: 0,
      scrollAreaHeight: 0,

      phoneHeight: 0,
      contentHeight: 0,

      contentStartY: 0,
      contentEndY: 0,
      phoneStartY: 0,
      phoneSettledY: 0,
      phoneEndY: 0,

      enterDistance: 1,
      revealDistance: 1,
      cardRevealMargin: 0,
      totalDistance: 1
    };

    this.onScroll = this.onScroll.bind(this);
    this.onResize = this.onResize.bind(this);
    this.onLoad = this.onLoad.bind(this);
  }

  connect() {
    this.measure();
    this.update();

    window.addEventListener("scroll", this.onScroll, { passive: true });
    window.addEventListener("resize", this.onResize);
    window.addEventListener("load", this.onLoad);
  }

  disconnect() {
    window.removeEventListener("scroll", this.onScroll);
    window.removeEventListener("resize", this.onResize);
    window.removeEventListener("load", this.onLoad);

    if (this.raf !== null) {
      cancelAnimationFrame(this.raf);
      this.raf = null;
    }
  }

  onLoad() {
    this.measure();
    this.update();
  }

  onScroll() {
    if (this.raf !== null) return;

    this.raf = requestAnimationFrame(() => {
      this.update();
      this.raf = null;
    });
  }

  onResize() {
    this.measure();
    this.update();
  }

  /**
   * @param {number} value
   * @param {number} min
   * @param {number} max
   * @returns {number}
   */
  clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
  }

  /**
   * @param {number} value
   * @param {number} start
   * @param {number} end
   * @returns {number}
   */
  mapProgress(value, start, end) {
    if (value <= start) return 0;
    if (value >= end) return 1;
    return (value - start) / (end - start);
  }

  /**
   * @param {number} start
   * @param {number} end
   * @param {number} progress
   * @returns {number}
   */
  lerp(start, end, progress) {
    return start + (end - start) * progress;
  }

  /**
   * Ease-out cubic
   * @param {number} t
   * @returns {number}
   */
  easeOutCubic(t) {
    return 1 - Math.pow(1 - t, 3);
  }

  syncOverlayAnchors() {
    if (
      !(this.scrollArea instanceof HTMLElement) ||
      !(this.phone instanceof HTMLElement) ||
      !(this.overlays instanceof HTMLElement)
    ) {
      return;
    }

    const scrollAreaRect = this.scrollArea.getBoundingClientRect();
    const phoneRect = this.phone.getBoundingClientRect();

    const phoneLeft = phoneRect.left - scrollAreaRect.left;
    const phoneTop = phoneRect.top - scrollAreaRect.top;
    const phoneWidth = phoneRect.width;
    const phoneHeight = phoneRect.height;

    this.overlays.style.setProperty("--tools-phone-left", `${phoneLeft}px`);
    this.overlays.style.setProperty("--tools-phone-top", `${phoneTop}px`);
    this.overlays.style.setProperty("--tools-phone-width", `${phoneWidth}px`);
    this.overlays.style.setProperty("--tools-phone-height", `${phoneHeight}px`);
  }

  measure() {
    if (
      !(this.scrollArea instanceof HTMLElement) ||
      !(this.scene instanceof HTMLElement) ||
      !(this.phone instanceof HTMLElement) ||
      !(this.content instanceof HTMLElement)
    ) {
      return;
    }

    const isDesktop = window.matchMedia("(min-width: 1024px)").matches;

    const phoneHeight = this.phone.offsetHeight;
    const contentHeight = this.content.offsetHeight;
    const sceneHeight = this.scene.offsetHeight;

    const topPadding = 24;
    const gapBetweenContentAndPhone = 48;
    const cardRevealMargin = 400;
    const cardSpacing = 500;
    const endPadding = 800;

    const revealDistance = Math.max(this.cards.length * cardSpacing, 1200);

    const combinedHeight = phoneHeight + gapBetweenContentAndPhone + contentHeight;
    const bottomOffset = isDesktop ? 120 : 80;
    const centerY = Math.max((sceneHeight - combinedHeight) / 2 + bottomOffset, topPadding);

    const phoneSettledY = centerY;
    const contentEndY = centerY - contentHeight - gapBetweenContentAndPhone;
    const phoneStartY = 0;
    const contentStartY = phoneHeight + gapBetweenContentAndPhone;
    const phoneEndY = phoneSettledY;

    const enterDistance = 1800;
    const totalDistance = enterDistance + cardRevealMargin + revealDistance + endPadding;
    const scrollAreaHeight = sceneHeight + totalDistance;

    this.metrics = {
      sceneHeight,
      scrollAreaHeight,

      phoneHeight,
      contentHeight,

      contentStartY,
      contentEndY,
      phoneStartY,
      phoneSettledY,
      phoneEndY,

      enterDistance,
      revealDistance,
      cardRevealMargin,
      totalDistance
    };

    this.section.style.setProperty("--tools-phone-start-y", `${phoneStartY}px`);
    this.section.style.setProperty("--tools-phone-settled-y", `${phoneSettledY}px`);
    this.section.style.setProperty("--tools-phone-end-y", `${phoneEndY}px`);
    this.section.style.setProperty("--tools-content-y", `${contentStartY}px`);

    if (isDesktop) {
      this.scrollArea.style.minHeight = `${scrollAreaHeight}px`;
    } else {
      this.scrollArea.style.minHeight = "";
    }

    this.syncOverlayAnchors();
  }

  update() {
    if (!(this.scrollArea instanceof HTMLElement)) return;

    const rect = this.scrollArea.getBoundingClientRect();
    const scrollPastStart = Math.max(-rect.top, 0);
    const isDesktop = window.matchMedia("(min-width: 1024px)").matches;

    const {
      enterDistance,
      revealDistance,
      totalDistance,
      phoneStartY,
      phoneSettledY,
      cardRevealMargin,
      contentStartY,
      contentEndY
    } = this.metrics;

    const localScroll = this.clamp(scrollPastStart, 0, totalDistance);

    const cardStartScroll = enterDistance + cardRevealMargin;
    const revealScroll = Math.max(localScroll - cardStartScroll, 0);
    const cardsProgress = isDesktop ? this.clamp(revealScroll / revealDistance, 0, 1) : 1;

    this.section.style.setProperty(
      "--tools-showcase-cards-progress",
      cardsProgress.toFixed(4)
    );

    const card1 = this.mapProgress(cardsProgress, 0.00, 0.25);
    const card2 = this.mapProgress(cardsProgress, 0.30, 0.60);
    const card3 = this.mapProgress(cardsProgress, 0.65, 0.95);

    this.cards.forEach((card, index) => {
      let visible = false;

      if (index === 0) visible = card1 > 0;
      if (index === 1) visible = card2 > 0;
      if (index === 2) visible = card3 > 0;
      if (index > 2) visible = cardsProgress > 0.95;

      card.classList.toggle("is-visible", visible);
    });

    if (isDesktop) {
      const enterProgress = enterDistance > 0 ? this.clamp(localScroll / enterDistance, 0, 1) : 1;
      const easedEnter = this.easeOutCubic(enterProgress);
      const phoneY = this.lerp(phoneStartY, phoneSettledY, easedEnter);
      const contentY = this.lerp(contentStartY, contentEndY, easedEnter);

      this.section.style.setProperty("--tools-phone-translate-y", `${phoneY}px`);
      this.section.style.setProperty("--tools-content-y", `${contentY}px`);

      const endImageOpacity = this.clamp((enterProgress - 0.88) / 0.06, 0, 1);
      this.section.style.setProperty("--tools-phone-end-image-opacity", `${endImageOpacity}`);
      this.section.style.setProperty("--tools-overlay-end-opacity", `${endImageOpacity}`);

      const staticOverlaysOpacity = 1 - this.clamp(enterProgress / 0.03, 0, 1);
      this.section.style.setProperty("--tools-static-overlays-opacity", `${staticOverlaysOpacity}`);
    } else {
      this.section.style.setProperty("--tools-phone-end-image-opacity", "0");
      this.section.style.setProperty("--tools-overlay-end-opacity", "0");
      this.section.style.setProperty("--tools-static-overlays-opacity", "1");
    }
    this.syncOverlayAnchors();
  }
}

document.querySelectorAll("[data-sticky-tools-showcase]").forEach((section) => {
  if (!(section instanceof HTMLElement)) return;

  const instance = new StickyToolsShowcaseController(section);
  instance.connect();
});